import { ShopStyled } from '../shopSetting/assets/shopStyled';
import { StyledProduct } from './assets/ProductStyled';
import Table from '../../app/components/table/Table';
import { productDefaultImage } from './assets/productsUtil';
import {
  useGetInventoryInsightsByFilterMutation,
  useGetInventoryInsightsYearlyMutation,
} from './productsApiSlice';
import { useEffect, useState } from 'react';
import StyledLoading from '../../app/components/loading/StyledLoading';
import StyledSearchInput from '../../app/components/styledComponents/StyledSearchInput';
import styled, { useTheme } from 'styled-components';
import { sub } from 'date-fns';
import CustomDateRangePicker from '../../app/components/datePicker/CustomDateRangePicker';
import moment from 'moment';
import StyledText from '../../app/components/styledComponents/StyledText';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { setAppContainerWidth } from '../../app/services/sideBar';
import { getLightenColor } from '../../utils/helper';

const InventoryInsights = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const openSideMenu = useSelector((state) => state.sideBar.opensidemenu);
  //<======== First Table =========>
  const [getInventoryInsightsYearly] = useGetInventoryInsightsYearlyMutation();
  const [getInventoryInsightsByFilter] =
    useGetInventoryInsightsByFilterMutation();
  const [isLoading, setIsLoading] = useState(false); //Setting the state for loading
  const [getInventoryInsightsYearlyData, setGetInventoryInsightsYearlyData] =
    useState([]); //Setting the state for yearly data of inventory (First Table)
  const [inventoryInsightsColumn, setInventoryInsightsColumn] = useState([]); // Setting the state for yearly data's Column of inventory (First Table)
  //<======== Second Table =========>
  const [
    getInventoryInsightsByFilterDate,
    setGetInventoryInsightsByFilterDate,
  ] = useState([]);
  const [tempProductData, setTempProductData] = useState([]);
  const [searchProduct, setSearchProduct] = useState(''); // Product Search Field (Second Table)
  const [listing, setListing] = useState({
    sort: { path: 'totalSales', order: 'DESC' },
  }); // Filter for sorting of the Data to be fetched based on date (Second Table)
  const [dateSelection, setDateSelection] = useState([
    {
      startDate: new Date(sub(new Date(), { months: 3 }).setHours(0, 0, 0, 0)),
      endDate: new Date().setHours(23, 59, 59, 999),
      key: 'selection',
    },
  ]); // Date selection for the Data to be fetched based on date (Second Table)

  useEffect(() => {
    if (openSideMenu) {
      dispatch(setAppContainerWidth(`calc(100% - 320px)`));
    }
  }, [openSideMenu]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const response = await getInventoryInsightsYearly({
        year: new Date().getFullYear(),
      });
      if (response && response?.data) {
        const { data } = response;
        const UpdatedData = convertInventoryToDataFormat(
          data?.objectData ?? [],
        );

        setGetInventoryInsightsYearlyData(UpdatedData ?? []);
        setInventoryInsightsColumn(createColumns(data?.objectData ?? []));
      } else {
        setGetInventoryInsightsYearlyData([]);
        setInventoryInsightsColumn(createColumns([]));
      }

      setIsLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      /* Inventory Insights By Filter */
      // Ensure dateSelection is defined and has valid dates
      if (dateSelection && dateSelection.length > 0) {
        const startDate = moment(dateSelection[0].startDate).format(
          'YYYY-MM-DD HH:mm:ss',
        );
        const endDate = moment(dateSelection[0].endDate).format(
          'YYYY-MM-DD HH:mm:ss',
        );

        // Construct the filter object
        const filter = {
          startDate,
          endDate,
        };

        // URL encode the filter object
        const encodedFilter = encodeURIComponent(JSON.stringify(filter));

        // This will log the URL-encoded filter

        // Fetch inventory insights by filter
        const responseByFilter = await getInventoryInsightsByFilter({
          filter: encodedFilter, // Use the encoded filter
        });

        if (responseByFilter && responseByFilter?.data) {
          const { data } = responseByFilter;

          setGetInventoryInsightsByFilterDate(data?.objectData ?? []);
          setTempProductData(data?.objectData ?? []);
        }
      } else {
        console.error('Invalid date selection');
      }
    };
    fetchData();
  }, [dateSelection]);

  useEffect(() => {
    const sortedData = [...getInventoryInsightsByFilterDate].sort((a, b) => {
      const { path, order } = listing.sort;

      // Convert to numbers for numeric comparison
      const valueA = parseFloat(a[path]) || 0;
      const valueB = parseFloat(b[path]) || 0;

      if (order.toLowerCase() === 'asc') return valueA - valueB;
      else return valueB - valueA;
    });

    setGetInventoryInsightsByFilterDate(sortedData);
  }, [listing]);

  const convertInventoryToDataFormat = (inventory) => {
    const metrics = [
      { label: 'Total Orders#', key: 'totalOrders' },
      { label: 'Total Sales$', key: 'totalSales', format: true },
      { label: 'Total Product Views', key: 'totalCustomerViews' },
      { label: 'Abandoned Carts', key: 'abandonedCarts' },
      { label: 'Average Daily Sale', key: 'avgDailySales', format: true },
      { label: 'Total Add to Carts', key: 'totalAddToCarts' },
      {
        label: 'Sales Growth in %',
        key: 'pctIncreaseFromLastMonth',
        isPercentage: true,
      },
      {
        label: 'Full Refunds',
        key: 'refundCount'
      },
      {
        label: 'Partial Refunds',
        key: 'partialRefundCount'
      },
      {
        label: 'Total Refunds$',
        key: 'refunds',
        format: true
      },
    ];
    try {
      const result = [];

      // Define the metrics we want to include in the output structure

      // Formatter for currency and percentage
      const currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      });

      const percentageFormatter = new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      // Iterate through each metric and construct the output format
      metrics.forEach((metric) => {
        const metricData = {
          values: metric.label,
        };

        inventory.forEach((item) => {
          const month = item.monthYear.split(' ')[0].toLowerCase().slice(0, 3); // Get month abbreviation
          let value = item[metric.key];

          // Format the value if needed (for currency and percentages)
          if (value === undefined || value === null) {
            value = '-';
          } else if (metric.format) {
            value = currencyFormatter.format(value);
          } else if (metric.isPercentage) {
            value = percentageFormatter.format(value / 100);
          }

          metricData[month] = value;
        });

        result.push(metricData);
      });

      return result;
    } catch (err) {
      console.log(err);
      return metrics;
    }
  };

  const createColumns = (inventory) => {
    try {
      // Create the fixed "Values" column
      const columns = [];

      // Preprocess monthYear into month keys and labels for columns
      // Get the current month and year for comparison

      const monthData = inventory.map((item) => {
        const [month, year] = item.monthYear.split(' ');
        return {
          monthKey: month.toLowerCase().slice(0, 3), // Get month abbreviation
          fullLabel: item.monthYear, // Use full monthYear for label
        };
      });

      // Create column definitions in a single pass
      const monthColumns = monthData.map((data, index) => ({
        id: index + 2, // Increment ID for each column
        key: data.monthKey,
        label: data.fullLabel,
        content: (rowData) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {rowData[data.monthKey] ?? '-'}
          </div>
        ),
      }));

      // Combine fixed column and month columns
      columns.push(...monthColumns);
      columns.reverse();
      columns.unshift({
        id: 1,
        key: 'values',
        label: 'Metric',
        fixed: true,
        content: ({ values }) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minWidth: 'max-content',
            }}
          >
            <div style={{ width: '100%' }}> {values ?? '-'}</div>
          </div>
        ),
      });
      return columns;
    } catch (err) {
      console.log(err);
      return [
        {
          id: 1,
          key: 'values',
          label: 'Metric',
          fixed: true,
          content: ({ values }) => (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                minWidth: 'max-content',
              }}
            >
              <div style={{ width: '100%' }}> {values ?? '-'}</div>
            </div>
          ),
        },
      ];
    }
  };

  const handleSearch = (e) => {
    setSearchProduct(e.target.value);
    const searchTerm = e.target.value;
    if (e.target.value === '') {
      handleReset();
    } else {
      // Assuming tempProductData is an array of product objects with productName and tags properties
      const filteredData = tempProductData.filter((item) => {
        const lowerCaseSearchTerm = searchTerm?.toLowerCase().trim();
        // Check if productName includes the searchTerm
        const nameMatches = item?.productName
          ?.toLowerCase()
          .includes(lowerCaseSearchTerm);

        const contributedMatches =
          lowerCaseSearchTerm.includes('added to catalog') &&
          item?.contributed === true;

        // Check if any of the tags' names include the searchTerm
        const tagsMatch = item?.tags?.some((tag) =>
          tag?.name?.toLowerCase().includes(lowerCaseSearchTerm),
        );

        // Return true if either condition matches
        return nameMatches || tagsMatch || contributedMatches;
      });
      setGetInventoryInsightsByFilterDate(filteredData);
    }
  };

  const handleReset = () => {
    setSearchProduct('');
    setGetInventoryInsightsByFilterDate(tempProductData);
    setListing({
      sort: { path: 'totalSales', order: 'DESC' },
    });
  };

  const CustomHeader = () => (
    <StyledSearchInput
      padding="8px 16px"
      SearchContainerClassName="w-100"
      style={{ minWidth: '100px' }}
      stroke={theme.dark70}
      value={searchProduct}
      searchIconClassName="search-icon dark40"
      placeHolder="Search by Product Name or Tag"
      $phFontSize="16px"
      setValue={setSearchProduct}
      onChange={handleSearch}
      onReset={handleReset}
      $phColor={'bodyMedium'}
      $iconColor="dark40"
      svgSize={'24px'}
      autoFocus={true}
    />
  );

  const productColumn = [
    {
      id: 1,
      key: 'allproducts',
      path: 'allproducts',
      labelComponent: <CustomHeader />,
      headerStyle: {
        borderTop: '0px solid transparent',
        paddingTop: '10px',
      },
      style: { borderTop: '1px solid #efefef', background: 'white' },
      content: (products) => (
        <div
          className="product"
          style={{
            gap: '0.5rem',
            width: 'max-content',
            paddingRight: '10px',
          }}
        >
          <img
            loading="lazy"
            src={
              products?.productImageUrl
                ? products?.productImageUrl
                : productDefaultImage
            }
            alt={products?.productName ?? '-'}
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '200px',
              width: 'max-content',
              gap: '0.5rem',
              wordWrap: 'break-word',
            }}
          >
            {products?.productName ?? '-'}{' '}
            <p
              style={{
                fontStyle: 'italic',
                fontSize: '14px',
                color: '#9E9C9C',
                margin: '0',
                whiteSpace: 'normal',
              }}
            >
              {products && products?.tags && products?.tags.length > 0
                ? products?.tags.map((data) => data?.name).join(', ')
                : ' '}
            </p>
            {products?.contributed && (
              <TopTagDivRight>
                <StyledText
                  color={'primaryLight2'}
                  $fontSize="13px"
                  $fontWeight="600"
                  className={`mb-0 margin-right`}
                  style={{ cursor: 'default' }}
                >
                  Added to Catalog
                </StyledText>
              </TopTagDivRight>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 2,
      key: 'totalSales',
      path: 'totalSales',
      label: 'Sales $',
      headerStyle: {
        borderBottom: '1px solid #d7d7d7',
        borderTop: '0px solid transparent',
        paddingTop: '10px',
        paddingRight: '0px',
      },
      style: {
        background: '#f7f7f7',
        borderTop: '1px solid #d7d7d7',
      },
      content: ({ totalSales }) => {
        const formattedSales =
          totalSales !== null || totalSales !== undefined
            ? new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(totalSales)
            : '-';

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {formattedSales}
          </div>
        );
      },
    },
    {
      id: 3,
      key: 'totalNoOfSales',
      path: 'totalNoOfSales',
      label: 'Sales #',
      headerStyle: {
        borderBottom: '1px solid #d7d7d7',
        borderTop: '0px solid transparent',
        paddingTop: '10px',
        paddingLeft: '0px',
        paddingRight: '0px',
      },
      style: {
        background: '#f7f7f7',
        borderTop: '1px solid #d7d7d7',
      },
      content: ({ totalNoOfSales }) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {totalNoOfSales ?? '-'}
        </div>
      ),
    },
    {
      id: 4,
      key: 'totalCustomerViews',
      path: 'totalCustomerViews',
      label: 'Views',
      headerStyle: {
        borderBottom: '1px solid #d7d7d7',
        borderTop: '0px solid transparent',
        paddingTop: '10px',
        paddingLeft: '0px',
        paddingRight: '0px',
      },
      style: {
        background: '#f7f7f7',
        borderTop: '1px solid #d7d7d7',
      },
      content: ({ totalCustomerViews }) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {totalCustomerViews ?? '-'}
        </div>
      ),
    },
    {
      id: 5,
      key: 'totalAddToCart',
      path: 'totalAddToCart',
      label: 'Add to Carts',
      headerStyle: {
        borderBottom: '1px solid #d7d7d7',
        borderTop: '0px solid transparent',
        paddingTop: '10px',
        wordWrap: 'break-word', // Ensures that the header text wraps onto the next line
        whiteSpace: 'normal', // Allows wrapping of the text
        maxWidth: '100%', // Ensures the header doesn't overflow
        paddingLeft: '0px',
        paddingRight: '0px',
      },
      style: {
        background: '#f7f7f7',
        borderTop: '1px solid #d7d7d7',
      },
      content: ({ totalAddToCart }) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {totalAddToCart ?? '-'}
        </div>
      ),
    },
    {
      id: 6,
      key: 'shopsAdded',
      path: 'shopsAdded',
      label: 'Shop Added',
      headerStyle: {
        borderTop: '0px solid transparent',
        paddingTop: '10px',
        wordWrap: 'break-word', // Ensures that the header text wraps onto the next line
        whiteSpace: 'normal', // Allows wrapping of the text
        maxWidth: '100%', // Ensures the header doesn't overflow
        paddingLeft: '10px',
        paddingRight: '0px',
      },
      style: { background: 'white', borderTop: '1px solid #efefef' },
      content: ({ shopsAdded }) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {shopsAdded ?? '-'}
        </div>
      ),
    },
    {
      id: 7,
      key: 'commissionEarned',
      path: 'commissionEarned',
      label: 'Commissions',
      headerStyle: {
        borderTop: '0px solid transparent',
        paddingTop: '10px',
        paddingLeft: '0px',
        paddingRight: '0px',
      },
      style: { background: 'white', borderTop: '1px solid #efefef' },
      content: ({ commissionEarned }) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {commissionEarned !== null || commissionEarned !== undefined
            ? new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(commissionEarned)
            : '-'}
        </div>
      ),
    },
    {
      id: 8,
      key: 'commissionSales',
      path: 'commissionSales',
      label: 'Sales #',
      headerStyle: {
        borderTop: '0px solid transparent',
        paddingTop: '10px',
        paddingLeft: '0px',
        paddingRight: '0px',
      },
      style: { background: 'white', borderTop: '1px solid #efefef' },
      content: ({ commissionSales }) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {commissionSales ?? '-'}
        </div>
      ),
    },
  ];

  return (
    <ShopStyled.PageWrap isFullWidth>
      <ShopStyled.BootstrapCard style={{ gap: '2rem' }}>
        {isLoading ? (
          <StyledLoading />
        ) : (
          <>
            {inventoryInsightsColumn.length == 0 ||
            getInventoryInsightsYearlyData.length == 0 ? (
              <ShopStyled.BootstrapCard style={{ gap: '2rem' }}>
                {' '}
                <StyledText
                  as="h4"
                  $fontSize="28px"
                  className="d-flex align-items-center justify-content-center my-3 my-md-5"
                >
                  No Data Found
                </StyledText>
              </ShopStyled.BootstrapCard>
            ) : (
              <StyledProduct.ProductsTable className="product-table">
                <Table
                  className={'border-0'}
                  columns={inventoryInsightsColumn}
                  highlightRowsOnHover
                  hidePagination
                  data={{ content: getInventoryInsightsYearlyData }}
                />
              </StyledProduct.ProductsTable>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'center',
              }}
            >
              <CustomDateRangePicker
                dateRangeSelection={dateSelection}
                onChange={(data) => setDateSelection(data)}
              />
            </div>

            <StyledProduct.ProductsTable>
              <div
                className="table-container"
                style={{
                  maxHeight: '1000px',
                }}
              >
                <Table
                  className={'border-0'}
                  columns={productColumn}
                  sortColumn={listing.sort}
                  onSort={(sort) => {
                    setListing((prev) => ({ ...prev, sort }));
                  }}
                  hidePagination
                  mainHeading={[
                    {
                      name: 'Products',
                      from: 1,
                      to: 1,
                      emptyHeadingStyle: {
                        borderBottom: '0px solid transparent',
                        backgroundColor: 'white',
                        paddingTop: '10px',
                        paddingBottom: '0px',
                      },
                      style: {
                        borderLeft: '1px solid #efefef',
                        borderBottom: '0px solid transparent',
                        backgroundColor: 'white',
                        paddingTop: '10px',
                        paddingBottom: '0px',
                      },
                    },
                    {
                      name: 'My Website',
                      from: 2,
                      to: 5,
                      emptyHeadingStyle: {
                        borderBottom: '0px solid transparent',
                        backgroundColor: 'white',
                        paddingTop: '10px',
                        paddingBottom: '0px',
                      },
                      style: {
                        borderLeft: '1px solid #efefef',
                        borderBottom: '0px solid transparent',
                        backgroundColor: '#f7f7f7',
                        paddingTop: '10px',
                        paddingBottom: '0px',
                      },
                    },
                    {
                      name: 'Catalog Performance',
                      from: 6,
                      to: 8,
                      emptyHeadingStyle: {
                        borderBottom: '0px solid transparent',
                        backgroundColor: 'white',
                        paddingTop: '10px',
                        paddingBottom: '0px',
                      },
                      style: {
                        borderLeft: '1px solid #efefef',
                        borderBottom: '0px solid transparent',
                        backgroundColor: 'white',
                        paddingTop: '10px',
                        paddingBottom: '0px',
                      },
                    },
                  ]}
                  data={
                    getInventoryInsightsByFilterDate &&
                    getInventoryInsightsByFilterDate.length > 0
                      ? { content: getInventoryInsightsByFilterDate ?? [] }
                      : []
                  }
                />
              </div>
            </StyledProduct.ProductsTable>
          </>
        )}
      </ShopStyled.BootstrapCard>
    </ShopStyled.PageWrap>
  );
};

const TopTagDivRight = styled.div`
  color: ${({ theme }) => theme.primaryLight2};
  background-color: ${({ theme }) => theme.primary};
  padding: 3px 13px;
  width: fit-content;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => getLightenColor('primaryDark', theme)};
`;

export default InventoryInsights;
